/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useEffect, useState } from 'react';
import HdModal from '../../../../modal/components/modal/HdModal';
import { AuthAccountEnvironmentMetaData } from '../../../../containers/pipeline/interfaces/source-type';
import {
  HdButton,
  HdIcon,
  HdIconButton,
  HdRadio,
  HdRadioGroup
} from '../../../../components';
import {
  getAuthEnvironmentLabel,
  getAuthEnvironmentDescription
} from './utils';
import { AuthAccountEnvironment } from './interface';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import { TRACKER_SELECT_OAUTH_ACCOUNT_ENVIRONMENT } from './constants';
import styles from '../auth-select/AuthSelect.module.scss';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';

export interface IAuthEnvironmentSelectionProps {
  providerDisplay: string;
  consentUrls: { env: AuthAccountEnvironment; url: string }[];
  authAccountEnvironmentsMetaData: { [key: string]: AuthAccountEnvironmentMetaData };
  isOpen: boolean;
  onClose: Function;
}

const generateDataId = getDataIdGenerator('auth-env-selector-dialog');

export function AuthEnvironmentSelection({
  providerDisplay,
  consentUrls = [],
  authAccountEnvironmentsMetaData,
  onClose,
  isOpen
}: IAuthEnvironmentSelectionProps) {
  const [selectedUrl, setSelectedUrl] = useState<{ env: AuthAccountEnvironment; url: string }>(
    consentUrls?.[0]
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedUrl(consentUrls?.[0]);
    } else {
      setSelectedUrl(null);
    }
  }, [isOpen]);

  const { eventTrack } = useAnalyticsTracker();

  const onContinueClick = () => {
    eventTrack({
      action: TRACKER_SELECT_OAUTH_ACCOUNT_ENVIRONMENT,
      properties: { env: selectedUrl.env, source_type: providerDisplay }
    });

    window.location.assign(selectedUrl.url);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <HdModal className={styles.selectEnvDialog} open={isOpen}>
      <div className='dialog-content' data-id={generateDataId('content')}>
        <div className='dialog-header dialog-header--absolute' tabIndex={0}>
          <HdIconButton dataId={generateDataId('close')} onClick={() => onClose()}>
            <HdIcon name='close' />
          </HdIconButton>
        </div>

        <div className='dialog-body'>
          <div className='text-subheading-3 mt-1 mb-1'>
            <span>Select {providerDisplay} Environment</span>
            <HdRadioGroup name='env' currentValue={selectedUrl?.url} onChange={() => {}}>
              {consentUrls.map(consentUrl => (
                <HdRadio
                  dataId={generateDataId(consentUrl.env)}
                  onChange={() => {
                    setSelectedUrl(consentUrl);
                  }}
                  className='mt-4'
                  value={consentUrl.url}
                >
                  <div className='ml-3 flex-col'>
                    <span className='text-default text-body-2'>
                      {getAuthEnvironmentLabel(consentUrl.env, authAccountEnvironmentsMetaData)}
                    </span>
                    <span className='text-body-1 mt-1 text-secondary'>
                      {getAuthEnvironmentDescription(
                        consentUrl.env,
                        authAccountEnvironmentsMetaData
                      )}
                    </span>
                  </div>
                </HdRadio>
              ))}
            </HdRadioGroup>
          </div>
        </div>

        <div className='dialog-footer'>
          <HdButton variation='outline' dataId={generateDataId('close')} onClick={() => onClose()}>
            Cancel
          </HdButton>

          <HdButton dataId={generateDataId('continue')} onClick={() => onContinueClick()}>Continue</HdButton>
        </div>
      </div>
    </HdModal>
  );
}
